<div class="layout-topbar">
  <!-- Left Section: Menu + Logo -->
  <div class="layout-topbar-left">
    <button
      class="topbar-menu-btn btn-icon-only btn-md btn-tertiary"
      (click)="handleSidebarToggle()"
    >
      <i class="ti ti-menu-2"></i>
    </button>
    <a class="layout-topbar-logo" routerLink="/dashboard">
      <span class="logo-text">Omnimedica</span>
    </a>
    <button class="topbar-fullscreen-btn" (click)="toggleFullscreen()">
      @if (isFullscreen()) {
        <i class="ti ti-arrows-minimize"></i>
      } @else {
        <i class="ti ti-arrows-maximize"></i>
      }
      <span class="fullscreen-text text-sm-medium">{{
        "fullscreen" | translate
      }}</span>
      <span class="fullscreen-kbd">F11</span>
    </button>
  </div>

  <!-- Center Section: Search -->
  <div class="layout-topbar-center">
    <div class="topbar-search" #searchWrap>
      <i class="ti ti-search"></i>

      <input
        #searchInput
        type="text"
        [formControl]="searchCtrl"
        [placeholder]="'search' | translate"
        autocomplete="off"
        (focus)="onSearchFocus()"
      />

      <button type="button" class="search-clear" (click)="clearSearch()">
        <i class="ti ti-x"></i>
      </button>

      @if (searchOpen()) {
        <div class="search-dropdown">
          @if (searchLoading()) {
            <div class="search-row muted">
              <span class="search-spinner" aria-label="Loading"></span>
            </div>
          }
          @if (searchError()) {
            <div class="search-row error">{{ searchError() }}</div>
          }
          @if (
            !searchLoading() && !searchError() && searchItems().length === 0
          ) {
            <div class="search-row muted">
              {{ "noResultsFound" | translate }}
            </div>
          }
          @if (searchItems().length > 0) {
            <div class="search-list">
              @for (p of searchItems(); track p.id) {
                <button
                  type="button"
                  class="search-item"
                  (click)="onSelectPatient(p)"
                >
                  <div class="name">
                    {{ p.lastName | personNameCase }}
                    {{ p.firstName | personNameCase }}
                  </div>

                  <div class="meta">
                    @if (p.cnp.trim()) {
                      <span class="meta-chip">
                        <i class="ti ti-id"></i>
                        <span>{{ p.cnp }}</span>
                      </span>
                    }

                    <span class="meta-chip">
                      <i class="ti ti-phone"></i>
                      <span>{{ p.phone }}</span>
                    </span>
                  </div>
                </button>
              }
            </div>
          }

          <div class="search-footer">
            <button
              type="button"
              ejs-button
              cssClass="e-outline e-block"
              iconCss="ti ti-user-plus"
              (click)="goToNewPatient()"
            >
              <span class="new-patient-text">{{
                "newPatient" | translate
              }}</span>
            </button>
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Right Section: Actions -->
  <div class="layout-topbar-right">
    <!-- Location Selector -->
    <button
      class="topbar-location-btn btn-secondary btn-md"
      (click)="showContextMenuModal()"
    >
      <i class="ti ti-map-pin"></i>
      <span class="location-text">
        <span>{{ locationName || ("selectALocation" | translate) }}</span>
        <span class="location-text-separator">/</span>
        <span>{{ companyName || ("selectACompany" | translate) }}</span>
      </span>
      <i class="ti ti-chevron-down"></i>
    </button>

    <!-- Divider -->
    <div class="topbar-divider"></div>

    <!-- Language Selector (Development Only) -->
    @if (isDevelopment) {
      <button
        ejs-dropdownbutton
        cssClass="btn-tertiary"
        [items]="items"
        (select)="handleMenuItemClick($event)"
      >
        <span>Language</span>
      </button>
    }

    <!-- Notifications -->
    <!-- <button
      type="button"
      class="topbar-notifications-btn btn-icon-only btn-md btn-tertiary"
      (click)="handleNotificationsbarToggle()"
    >
      <i class="ti ti-message"></i>
      <span class="notification-badge">12</span>
    </button> -->

    <!-- Report a problem -->
    <button
      type="button"
      class="topbar-messaging-btn btn-md btn-tertiary"
      data-support-capture-toggle
      data-testid="topbar-report-problem"
      [title]="'Support_TicketCapture:title' | translate"
      (click)="toggleTicketCapture()"
    >
      <i class="ti ti-lifebuoy"></i>
    </button>

    <!-- Messaging -->
    <button
      type="button"
      class="topbar-messaging-btn btn-md btn-tertiary"
      data-messaging-toggle
      (click)="toggleMessagingSlideIn()"
    >
      <i class="ti ti-message-circle"></i>
      @if (totalUnread() > 0) {
        <span class="messaging-badge">{{ totalUnread() }}</span>
      }
    </button>

    <!-- User Avatar -->
    <div class="topbar-user-menu" #userMenuWrap>
      <button
        class="topbar-user-btn btn-tertiary btn-md"
        (click)="toggleUserMenu()"
      >
        <div class="user-avatar">{{ userInitials() }}</div>
      </button>

      @if (userMenuOpen()) {
        <div class="user-menu-dropdown">
          <div class="user-menu-header">
            <div class="user-menu-avatar">{{ userInitials() }}</div>
            <div class="user-menu-identity">
              <div class="user-menu-name">{{ userFullName() }}</div>
              @if (userEmail()) {
                <div class="user-menu-email">{{ userEmail() }}</div>
              }
            </div>
          </div>

          <div class="user-menu-timezone">
            <i class="ti ti-clock"></i>
            <span>{{ currentTimezone() }}</span>
          </div>

          <div class="user-menu-footer">
            <button
              type="button"
              ejs-button
              cssClass="e-outline e-block"
              iconCss="ti ti-logout"
              (click)="logout()"
            >
              {{ "logout" | translate }}
            </button>
          </div>
        </div>
      }
    </div>
  </div>

  <o3-structure-context-menu-modal
    #contextMenuModal
  ></o3-structure-context-menu-modal>

  <o3-communication-messaging-slide-in
    [isOpen]="isMessagingSlideInOpen()"
    (closed)="closeMessagingSlideIn()"
    (conversationOpened)="onSlideInConversationOpened($event)"
  />

  <o3-support-ticket-capture-widget
    [isOpen]="isTicketCaptureOpen()"
    (closed)="closeTicketCapture()"
  />
</div>
